import { styled } from '@mui/material/styles';
import {
  Box,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  Typography,
  Button,
  List,
  ListItem,
  Link,
  OutlinedInput,
  alpha,
} from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import logo from '../../../containers/member/assets/img/logo-powered-by.svg';
import { usePortalName } from '../../../hooks/usePortalName';
import { useNavigate } from 'react-router-dom';

const languages = ['EN', 'ES'];

const PREFIX = 'LandingPage';

const classes = {
  container: `${PREFIX}-container`,
  heroContent: `${PREFIX}-heroContent`,
  heroText: `${PREFIX}-heroText`,
  heroTitle1: `${PREFIX}-heroTitle1`,
  heroTitle2: `${PREFIX}-heroTitle2`,
  topHeader: `${PREFIX}-topHeader`,
  language: `${PREFIX}-language`,
  heroDescription: `${PREFIX}-heroDescription`,
  heroCTA: `${PREFIX}-heroCTA`,
  ctaBtn: `${PREFIX}-ctaBtn`,
  logoLight: `${PREFIX}-logoLight`,
  logoPrimary: `${PREFIX}-logoPrimary`,
  mainContent: `${PREFIX}-mainContent`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  listItem: `${PREFIX}-listItem`,
  footer: `${PREFIX}-footer`,
  footerMenu: `${PREFIX}-footerMenu`,
  link: `${PREFIX}-link`,
  scheduleBtn: `${PREFIX}-scheduleBtn`,
  sectionHeader: `${PREFIX}-sectionHeader`,
  sectionSubtitle: `${PREFIX}-sectionSubtitle`,
  sectionParagraph: `${PREFIX}-sectionParagraph`,
  subHeader: `${PREFIX}-subHeader`,
  labelUppercase: `${PREFIX}-labelUppercase`,
  reservationLine: `${PREFIX}-reservationLine`,
  boldInline: `${PREFIX}-boldInline`,
  scheduleWarning: `${PREFIX}-scheduleWarning`,
  contactNumber: `${PREFIX}-contactNumber`,
  footerBox: `${PREFIX}-footerBox`,
};

const portalName = usePortalName();
const heroImage = `${portalName}/home_hero.webp`;
const logoLight = `/${portalName}/brand-logo-light.svg`;
const logoPrimary = `/${portalName}/brand-logo-primary.svg`;
const logoLanding = `/${portalName}/brand-logo-landing.svg`;

interface RootProps {
  heroImage: string;
}

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'heroImage',
})<RootProps>(({ theme, heroImage }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: theme.palette.info,
    width: '100%',
    minHeight: '100vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },

  [`& .${classes.heroContent}`]: {
    display: 'flex',
    flex: '0 1 100%',
    flexDirection: 'column',
    backgroundSize: 'cover',
    backgroundImage: `url(${heroImage})`,
    padding: `${theme.spacing(5)} ${theme.spacing(10)} ${theme.spacing(11.25)}`,
    [theme.breakpoints.down('md')]: {
      padding: `${theme.spacing(5)} ${theme.spacing(2.5)} ${theme.spacing(5)}`,
    },
  },

  [`& .${classes.heroTitle1}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    color: theme.palette.buttonColor.main,
  },

  [`& .${classes.heroTitle2}`]: {
    fontFamily: 'Bitter',
    whiteSpace: 'pre-line',
    fontWeight: 600,
    fontSize: 72,
    lineHeight: theme.spacing(10),
    color: theme.palette.slate.main,
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      fontSize: 36,
      lineHeight: theme.spacing(6),
    },
  },

  [`& .${classes.topHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(5),
  },

  [`& .${classes.language}`]: {
    height: 36,
    width: 225,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.slate.main, 0.3)}`,
    '& .MuiSelect-select': {
      color: theme.palette.slate.main,
      paddingTop: theme.spacing(0.5),
    },
    [theme.breakpoints.down('md')]: {
      width: 180,
    },
  },

  [`& .${classes.heroDescription}`]: {
    fontFamily: 'Source Sans Pro',
    fontWeight: 400,
    fontSize: 24,
    color: theme.palette.lightTextPrimary.main,
    lineHeight: theme.spacing(4),
    width: 570,
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '50%',
      fontSize: 18,
      lineHeight: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },
  },

  [`& .${classes.heroCTA}`]: {
    display: 'flex',
    marginTop: theme.spacing(5),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },

  [`& .${classes.ctaBtn}`]: {
    borderRadius: 0,
    minWidth: 144,
    padding: `${theme.spacing(1.5)} ${theme.spacing(2.75)}`,
    [theme.breakpoints.down('md')]: {
      width: 250,
    },
  },

  [`& .${classes.scheduleBtn}`]: {
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.slate.main,
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: 0,
    marginBottom: theme.spacing(6),
    padding: `${theme.spacing(1.5)} ${theme.spacing(2.75)}`,
    minWidth: 160,
    '&:hover': {
      backgroundColor: theme.palette.white04p.main,
      border: `1px solid ${theme.palette.common.white}`,
    },
    [theme.breakpoints.down('md')]: {
      width: 250,
    },
  },

  [`& .${classes.logoPrimary}`]: {
    maxHeight: 80,
    [theme.breakpoints.down('md')]: {
      height: 60,
    },
  },

  [`& .${classes.logoLight}`]: {
    maxHeight: 62,
    marginTop: theme.spacing(7.5),
    '& img': {
      maxHeight: 62,
    },

    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },

  [`& .${classes.mainContent}`]: {
    display: 'flex',
    flex: '0 1 100%',
    padding: theme.spacing(9, 10),
    backgroundColor: theme.palette.slate.main,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      padding: theme.spacing(6.25, 2.5),
    },
  },

  [`& .${classes.left}`]: {
    flex: 7,
  },

  [`& .${classes.right}`]: {
    flex: 5,
    paddingLeft: theme.spacing(10),
    [theme.breakpoints.down('md')]: {
      paddingLeft: 0,
      margin: `${theme.spacing(4)} 0`,
    },
  },

  [`& .${classes.sectionHeader}`]: {
    fontSize: 36,
    fontWeight: 700,
    fontFamily: 'Bitter',
    color: theme.palette.secondary.light,
    lineHeight: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      fontSize: 30,
      lineHeight: theme.spacing(5),
    },
  },

  [`& .${classes.sectionSubtitle}`]: {
    fontSize: 24,
    fontWeight: 400,
    lineHeight: theme.spacing(4),
    marginTop: theme.spacing(3),
    fontFamily: 'Source Sans Pro',
    color: theme.palette.secondary.light,
    [theme.breakpoints.down('md')]: {
      fontSize: 18,
      lineHeight: theme.spacing(3),
    },
  },

  [`& .${classes.sectionParagraph}`]: {
    fontSize: 18,
    fontWeight: 400,
    lineHeight: theme.spacing(4),
    marginTop: theme.spacing(2),
    fontFamily: 'Source Sans Pro',
    color: theme.palette.secondary.light,
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('md')]: {
      fontSize: 18,
      lineHeight: theme.spacing(3),
    },
  },

  [`& .${classes.subHeader}`]: {
    fontSize: 21,
    fontWeight: 700,
    fontFamily: 'Bitter',
    color: theme.palette.secondary.light,
    lineHeight: theme.spacing(4),
  },

  [`& .${classes.labelUppercase}`]: {
    fontSize: 15,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginTop: theme.spacing(1),
    fontFamily: 'Source Sans Pro',
    letterSpacing: 1,
    color: theme.palette.secondary.light,
    whiteSpace: 'pre-line',
  },

  [`& .${classes.reservationLine}`]: {
    fontSize: 21,
    fontWeight: 400,
    fontFamily: 'Source Sans Pro',
    color: theme.palette.secondary.light,
    lineHeight: theme.spacing(4),
    marginBottom: theme.spacing(2),
    whiteSpace: 'pre-line',
    [theme.breakpoints.down('md')]: {
      fontSize: 16,
      lineHeight: theme.spacing(3),
    },
  },

  [`& .${classes.boldInline}`]: {
    fontWeight: 600,
  },

  [`& .${classes.scheduleWarning}`]: {
    marginTop: theme.spacing(8),
    color: theme.palette.secondary.light,
    maxWidth: 470,
    [theme.breakpoints.down('md')]: {
      fontSize: 21,
      lineHeight: theme.spacing(4),
      marginTop: 0,
    },
  },

  [`& .${classes.listItem}`]: {
    display: 'list-item',
    color: theme.palette.secondary.light,
    marginLeft: theme.spacing(3.75),
    fontSize: 21,
    padding: 0,
    lineHeight: theme.spacing(3.5),

    '&::marker': {
      fontSize: 16,
    },

    [theme.breakpoints.down('md')]: {
      fontSize: 16,
    },
  },

  [`& .${classes.footer}`]: {
    padding: `${theme.spacing(6.25)} ${theme.spacing(7.5)}`,
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    backgroundColor: theme.palette.text.primary,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      height: 'fit-content',
      paddingTop: theme.spacing(6.25),
      paddingBottom: theme.spacing(6.25),
      alignItems: 'start',
    },
  },

  [`& .${classes.footerMenu}`]: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'start',
      marginTop: theme.spacing(2.5),
    },
  },

  [`& .${classes.link}`]: {
    fontFamily: 'Source Sans Pro',
    color: theme.palette.secondary.light,
    fontSize: 16,
    textDecoration: 'none',
    marginLeft: theme.spacing(0.625),
    marginRight: theme.spacing(2.5),
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(2.5),
      marginLeft: 0,
    },
  },

  [`& .${classes.contactNumber}`]: {
    fontSize: 21,
    fontWeight: 600,
    lineHeight: theme.spacing(4),
    letterSpacing: '0.25px',
    color: theme.palette.secondary.light,
    textDecoration: 'none',
  },

  [`& ul.MuiList-root`]: {
    listStyleType: 'disc',
  },

  [`& .${classes.footerBox}`]: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(0.625),
  },
}));

export default function LandingPage() {
  const t = useCommonTranslation();
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();

  const transportOptions = t('landing.transportOptionsList', {
    returnObjects: true,
  }) as string[];

  return (
    <Root className={classes.container} heroImage={heroImage}>
      <Box className={classes.heroContent} component="header">
        <Box className={classes.topHeader}>
          <Box
            component="img"
            alt={`${t('member.logoAlt')}`}
            src={logoLanding}
            className={classes.logoPrimary}
          />
          {/* TODO: Add language selector back in once https://americanlogistics.atlassian.net/browse/F1V2-916 is completed */}
          {/* <Box>
            <Select
              className={classes.language}
              defaultValue="EN"
              IconComponent={KeyboardArrowDownIcon}
              input={
                <OutlinedInput
                  inputProps={{
                    'aria-label': t('landing.selectLanguage'),
                  }}
                />
              }>
              {languages.map((language) => (
                <MenuItem key={language} value={language}>
                  {t(`languages.${language}`)}
                </MenuItem>
              ))}
            </Select>
          </Box> */}
        </Box>
        <Box className={classes.heroText}>
          <Typography variant="h3" className={classes.heroTitle1}>
            {t('landing.title')}
          </Typography>
          <Typography
            variant="h2"
            component="h1"
            className={classes.heroTitle2}>
            {t('landing.heroTitle')}
          </Typography>
          <Typography
            variant="h4"
            component="p"
            className={classes.heroDescription}>
            {t('landing.sectionSubtitle')}
          </Typography>
        </Box>
        <Box className={classes.heroCTA}>
          <Button
            variant="contained"
            size="large"
            className={classes.ctaBtn}
            onClick={() => navigate('/login')}>
            {t('landing.scheduleTransportation')}
          </Button>
        </Box>
      </Box>
      <Box className={classes.mainContent} component="main">
        <Box className={classes.left}>
          <Typography component="h2" className={classes.sectionHeader}>
            {t('landing.sectionHeader')}
          </Typography>
          <Typography className={classes.sectionSubtitle}>
            {t('landing.sectionSubtitle')}
          </Typography>
          <Typography className={classes.sectionParagraph}>
            {t('landing.sectionParagraph')}
          </Typography>
          <Typography variant="h3" className={classes.subHeader}>
            {t('landing.howToScheduleTransportation')}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            className={classes.labelUppercase}>
            {t('landing.byPhone')}
          </Typography>
          <Typography className={classes.reservationLine}>
            {t('landing.memberReservationLine')}
            <Box component="span" className={classes.boldInline}>
              {t('landing.americanLogisticsTelephone')}
            </Box>{' '}
            – {t('landing.alCallCenter')}
          </Typography>
          <Typography
            variant="h6"
            component="p"
            className={classes.labelUppercase}>
            {t('landing.byWeb')}
          </Typography>
          <Typography className={classes.reservationLine}>
            {t('landing.scheduleTransportationWeb')}
          </Typography>
          <Button
            variant="outlined"
            size="large"
            className={classes.scheduleBtn}
            onClick={() => navigate('/login')}>
            {t('landing.scheduleHere')}
          </Button>

          {isLarge && <ContactInformation />}

          <Typography
            variant="h2"
            component="p"
            className={classes.scheduleWarning}>
            {t('landing.scheduleTransportationWarning')}
          </Typography>

          <Box className={classes.logoLight}>
            <Box
              component="img"
              alt={`${t('member.logoAlt')}`}
              src={logoLight}
            />
          </Box>
        </Box>
        <Box className={classes.right}>
          <Typography variant="h3" className={classes.subHeader}>
            {t('landing.transportationOptionsHeader')}
          </Typography>

          <List>
            {transportOptions?.map &&
              transportOptions?.map((item, index) => (
                <ListItem key={index} className={classes.listItem}>
                  {item}
                </ListItem>
              ))}
          </List>
        </Box>

        {!isLarge && <ContactInformation />}
      </Box>

      <Box className={classes.footer} component="footer">
        <Box component="img" alt="American Logistics" src={logo} />
        <Box className={classes.footerMenu}>
          <Box component="div" className={classes.footerBox}>
            <Typography color="secondary">{t('footer.questions')}</Typography>
          </Box>
          <Link
            href="https://americanlogistics.com/privacy/"
            target="_blank"
            className={classes.link}
            color="secondary">
            {t('footer.privacy')} {t('footer.terms')}
          </Link>
        </Box>
      </Box>
    </Root>
  );
}

function ContactInformation() {
  const t = useCommonTranslation();

  return (
    <>
      <Typography variant="h3" className={classes.subHeader}>
        {t('landing.contactInformation')}
      </Typography>

      <Typography variant="h6" component="p" className={classes.labelUppercase}>
        {t('landing.memberHealthCare')}
        <Typography component="span" className={classes.contactNumber}>
          {t('landing.memberTelephone')}
        </Typography>
      </Typography>

      <Typography variant="h6" component="p" className={classes.labelUppercase}>
        {t('landing.americanLogistics')}
        <Typography component="span" className={classes.contactNumber}>
          {t('landing.americanLogisticsTelephone')}
        </Typography>
      </Typography>
    </>
  );
}
